import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { AkamLogoCheckIcon, AkamLogoTextIcon, HamburgerIcon, LoginIcon } from 'icons';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { useAppDispatch, useAppSelector } from 'store';

import { globalConst, setLoading, setUser, useScrollHeader, useUserData } from 'modules/global';

import { HeartWrapper, Visible } from 'components';

import LanguageDropdown from './language-dropdown';
import MobileDrawer from './mobile-drawer';
import ProfileDropDown from './profile-dropdown';

type NavItemType = {
  label: string;
  href: string;
  isNew?: boolean;
};

const Header = () => {
  const { pathname, locale = 'uz' } = useRouter();
  const dispatch = useAppDispatch();
  const { user, isLoading, isFetching } = useUserData();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { showHeader } = useScrollHeader();
  const t = globalConst.extraTranslation.header[locale as 'uz' | 'ru'];

  const userData = useAppSelector(state => state.user);

  useEffect(() => {
    dispatch(setLoading(isLoading || isFetching));
    if (user?.phone) {
      dispatch(setUser(user));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoading, isFetching]);

  const navItems: NavItemType[] = [
    { label: t.bosh_sahifa, href: '/' },
    { label: t.mavzuli_test, href: globalConst.pageNames.TOPIC_TEST },
    { label: t.blok_test, href: globalConst.pageNames.BLOCK_TEST },
    { label: t.oliygohlar, href: globalConst.pageNames.UNIVERSITIES },
    { label: t.yangiliklar, href: globalConst.pageNames.NEWS },
    { label: t.xususiy_universitetlar, href: globalConst.pageNames.PRIVATE_UNIVERSITIES, isNew: true }
  ];

  return (
    <header
      className={classNames('sticky top-0 z-50 w-full bg-white shadow-sm transition-transform duration-300', {
        'pointer-events-auto translate-y-0': showHeader,
        'pointer-events-none -translate-y-full': !showHeader
      })}
    >
      <MobileDrawer navItems={navItems} open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <div className="container mx-auto flex min-h-14 max-w-[1254px] items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2">
          <div
            role="button"
            aria-label="Open mobile menu"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && setDrawerOpen(true)}
            onClick={() => setDrawerOpen(true)}
            className="flex cursor-pointer lg:hidden"
          >
            <HamburgerIcon />
          </div>
          <Link href="/" aria-label="Go to homepage" className="flex items-center space-x-2 text-xl font-bold">
            <AkamLogoCheckIcon aria-label="Akam Logo" />
            <AkamLogoTextIcon aria-label="Akam logo text" />
          </Link>
        </div>

        <div className="flex items-center space-x-4">
          {user && (
            <div>
              <HeartWrapper box={false} iconSize="30" />
            </div>
          )}

          <LanguageDropdown />
          <Visible
            if={isLoading || isFetching}
            else={
              <Visible
                if={user?.phone}
                else={
                  <Link
                    href={globalConst.pageNames.LOGIN}
                    aria-label={t.kirish || 'Kirish'}
                    className="w-24.5 flex cursor-pointer items-center space-x-2 rounded-lg bg-green-500 px-4 py-1.5 text-white"
                  >
                    <LoginIcon />
                    <span>{t.kirish}</span>
                  </Link>
                }
              >
                <ProfileDropDown
                  userName={`${userData.name.slice(0, 1)}${userData?.last_name !== null ? userData?.last_name.slice(0, 1) : ''}`}
                  userImage={userData.image}
                />
              </Visible>
            }
          >
            <div className="w-24.5 h-9 animate-pulse rounded-lg bg-gray-200" />
          </Visible>
        </div>
      </div>
      <hr className="!border-.5 !my-1 !border-gray-200 " />
      <ul className="mx-auto hidden max-w-[1254px] space-x-6 px-3 py-2 text-gray-800 lg:flex">
        {navItems.map(item => {
          const isActive = item.href === '/' ? pathname === '/' : pathname.startsWith(item.href);

          return (
            <li key={item.href}>
              <Link
                href={item.href}
                className={`relative flex items-center gap-2 ${isActive ? 'font-medium text-green-600' : 'hover:text-green-600'}`}
              >
                <span className="flex items-center gap-1 text-sm">
                  {item.label}
                  {item.isNew && (
                    <span className="relative -top-2 right-0.5 flex items-center justify-center rounded-full bg-gray-800 px-1.5 py-0.5 text-[0.625rem] font-medium text-white">
                      {t.yangi}
                    </span>
                  )}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </header>
  );
};

export default Header;
